import {
    Alert,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    DialogContent,
    Fade,
    IconButton,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import ProfileCard_ttx from "./ProfileCard_ttx";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { submitFeedbackCorporate } from "../../../../APIConfig/NewCorporateApiConfig";
import { toast } from "react-toastify";
import { loadingButtonClasses } from "@mui/lab";


// ── Constants ─────────────────────────────────────────────────────────────
const QUESTIONS_A = [
    { id: "q1", num: 1, text: "The cyber exercise scenarios were realistic and relevant to real-world cyber threats faced by banks." },
    { id: "q2", num: 2, text: "The exercise effectively tested your bank's preparedness and decision-making under pressure." },
    { id: "q3", num: 3, text: "The sequence and timing of injects were appropriate and well-coordinated throughout the drill." },
    { id: "q4", num: 4, text: "The exercise resulted in clear learning outcomes for participating teams." },
    { id: "q5", num: 5, text: "The Cyber Range platform was stable and accessible throughout the exercise." },
    { id: "q6", num: 6, text: "The platform interface and navigation were intuitive and easy to use." },
    { id: "q7", num: 7, text: "Overall experience of the Cyber Exercise." },
];

const EMPTY_FORM = {
    scenario: "",
    exercise_date: "",
    section_a: {
        q1: null, q2: null, q3: null,
        q4: null, q5: null, q6: null, q7: null,
    },
    section_b: {
        gap1: "", gap2: "", gap3: "", gap4: "", gap5: "",
        scenario_changes: "",
        platform_feedback: "",
        additional_tasks: "",
    },
};

// ── Sub-components ────────────────────────────────────────────────────────
function SectionLabel({ children }) {
    return (
        <Typography
            sx={{
                fontSize: "0.62rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#ACACAC",
                fontFamily: "Poppins",
            }}
        >
            {children}
        </Typography>
    );
}

function RatingRow({ num, text, field, value, onChange }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                py: 1.5,
                borderBottom: "1px solid #2A2C35",
                "&:last-child": { borderBottom: "none" },
            }}
        >
            <Typography
                sx={{ color: "#00FFFF", fontWeight: 700, fontSize: "0.82rem", minWidth: 22 }}
            >
                {num}
            </Typography>
            <Typography
                sx={{ flex: 1, fontSize: "0.83rem", color: "#EAEAEB", lineHeight: 1.5 }}
            >
                {text}
            </Typography>
            <ToggleButtonGroup
                exclusive
                value={value}
                onChange={(_, v) => onChange(field, v)}
                sx={{ flexShrink: 0 }}
            >
                {[1, 2, 3, 4, 5].map((v) => (
                    <ToggleButton
                        key={v}
                        value={v}
                        sx={{
                            border: "1px solid #12464C !important",
                            color: "#ACACAC",
                            fontWeight: 600,
                            minWidth: 40,
                            height: 34,
                            fontSize: "0.8rem",
                            "&.Mui-selected": {
                                background: "rgba(0, 255, 255, 0.20)",
                                borderColor: "#0FF !important",
                                color: "#00FFFF",
                                "&:hover": { background: "rgba(0, 204, 204, 0.20)" },
                            },
                            "&:hover": { borderColor: "#0FF !important", color: "#00FFFF" },
                        }}
                    >
                        {v}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>
    );
}


export default function FeedbackForm({ open, onClose, onSubmit, name, scenario, participant_id, team }) {

    const navigate = useNavigate()
    const { id } = useParams()
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    // ── Field setters ──────────────────────────────────────────────────────
    const setRating = (field, value) =>
        setForm((f) => ({ ...f, section_a: { ...f.section_a, [field]: value } }));
    const setGap = (i, value) =>
        setForm((f) => ({
            ...f,
            section_b: { ...f.section_b, [`gap${i + 1}`]: value },
        }));
    const setB = (field, value) =>
        setForm((f) => ({ ...f, section_b: { ...f.section_b, [field]: value } }));

    // ── Submit handler ─────────────────────────────────────────────────────


    const handleReset = () => {
        if (window.confirm("Reset all fields?")) {
            setForm(EMPTY_FORM);
            setErrors([]);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }

    }


    const handleSubmit = async () => {

        const errs = [];
        // required ratings
        QUESTIONS_A.forEach(q => {
            if (!form.section_a[q.id]) {
                errs.push(`Question ${q.num} is required`);
            }
        });

        // required gap1
        if (!form.section_b.gap1?.trim()) {
            errs.push("Gap 1 is required");
        }

        if (errs.length) {
            setErrors(errs);
            return;
        }

        setErrors([]);
        setLoading(true);

        // ---------------- questions array ----------------
        const questions = QUESTIONS_A.map(q => ({
            id: q.id,
            question: q.text,
            answer: form.section_a[q.id]
        }));

        // ---------------- gaps array ----------------
        const gaps = [1, 2, 3, 4, 5]
            .map(i => ({
                id: `gap${i}`,
                question: `Gap ${i}`,
                answer: form.section_b[`gap${i}`]
            }))
            .filter(g => g.answer?.trim());

        // ---------------- additional feedback array ----------------
        const additional_feedback = [
            {
                id: "scenario_changes",
                question: "Changes / enhancements which you may suggest to the exercise scenarios.",
                answer: form.section_b.scenario_changes
            },
            {
                id: "platform_feedback",
                question: "Feedback / suggestion on the platform used for the Cyber Exercise.",
                answer: form.section_b.platform_feedback
            },
            {
                id: "additional_tasks",
                question: "Any additional task your team would have done, which was not executed during the exercise.",
                answer: form.section_b.additional_tasks
            }
        ].filter(a => a.answer?.trim());

        const payload = {
            feedback: {
                questions,
                gaps,
                additional_feedback
            }
        };


        try {
            if (onSubmit) {
                await onSubmit(payload);
            }
            setLoading(true)
            const response = await submitFeedbackCorporate(id, participant_id, payload)
            if (response?.data) {
                setSubmitted(true);
            }

        } catch (error) {
            setErrors(["Network error. Please try again."]);
            toast.error(error?.response?.data?.errors);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setForm(EMPTY_FORM);
        setErrors([]);
        setSubmitted(false);
        onClose?.();
    };

    // ── Shared card style ──────────────────────────────────────────────────
    const cardSx = {
        border: "1px solid #12464C",
        background: "#16181F",
        borderRadius: 2,
        mb: 2.5,
    };

    // ── Success state ──────────────────────────────────────────────────────
    const SuccessView = (
        <Box sx={{ textAlign: "center", py: 8, px: 4 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "#00FFFF", mb: 2 }} />
            <Typography variant="h3" sx={{ mb: 1 }}>Feedback Submitted</Typography>
            <Typography variant="h5" sx={{ color: "#ACACAC", mb: 4 }}>
                Thank you. Your response has been recorded anonymously.
            </Typography>


            <Button variant="outlined" onClick={() => navigate('/scenarios/active-scenarios')}> Go back to active scenarios</Button>
        </Box >
    );

    // ── Form body ──────────────────────────────────────────────────────────
    const FormBody = (
        <Box>
            {/* Header */}
            <Box
                sx={{
                    px: 3, pt: 3, pb: 2,
                    borderBottom: "1px solid #12464C",
                    background: "#16181F",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                }}
            >
                <Typography variant="h2" sx={{ mt: 0.5 }}>
                    Participant Feedback Form
                </Typography>
                <Typography variant="h5" sx={{ mt: 1, color: "#ACACAC", lineHeight: 1.6 }}>
                    Please provide feedback based on your experience. This feedback is anonymous. One entry per team.
                </Typography>
                {/* Errors */}
                <Collapse in={errors.length > 0}>
                    <Alert
                        severity="error"
                        sx={{ mt: 2, background: "#310f0f", border: "1px solid #E53935", borderRadius: 1 }}
                    >
                        {errors.map((e, i) => <div key={i}>{e}</div>)}
                    </Alert>
                </Collapse>
            </Box>

            <Box sx={{ px: 3, py: 2.5 }}>

                {/* Section A */}
                <Paper variant="outlined" sx={cardSx}>
                    <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #12464C" }}>
                        <SectionLabel>Section A — Quantitative Feedback</SectionLabel>
                        <Typography sx={{ fontSize: "0.72rem", color: "#ACACAC", mt: 0.5 }}>
                            1 = Strongly Disagree &nbsp;|&nbsp; 2 = Disagree &nbsp;|&nbsp; 3 = Neutral &nbsp;|&nbsp; 4 = Agree &nbsp;|&nbsp; 5 = Strongly Agree
                        </Typography>
                    </Box>
                    <Box sx={{ px: 2 }}>
                        {QUESTIONS_A.map((q) => (
                            <RatingRow
                                key={q.id}
                                num={q.num}
                                text={q.text}
                                field={q.id}
                                value={form.section_a[q.id]}
                                onChange={setRating}
                            />
                        ))}
                    </Box>
                </Paper>

                {/* Section B */}
                <Paper variant="outlined" sx={cardSx}>
                    <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #12464C" }}>
                        <SectionLabel>Section B — Qualitative Feedback</SectionLabel>
                    </Box>
                    <Box sx={{ p: 2 }}>

                        {/* Q8 – SOP Gaps */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h5" sx={{ mb: 1.5 }}>
                                <Box component="span" sx={{ color: "#00FFFF", fontWeight: 700 }}>08. </Box>
                                Gaps in your bank's existing SOPs identified during the exercise.{" "}
                                <Box component="span" sx={{ color: "#ACACAC" }}>(Highlight top five gaps pointwise)</Box>
                            </Typography>
                            <Stack spacing={1.25}>
                                {[1, 2, 3, 4, 5].map((num, i) => (
                                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <Typography sx={{ fontSize: "0.78rem", color: "#ACACAC", minWidth: 20 }}>
                                            {num}.
                                        </Typography>
                                        <TextField
                                            fullWidth size="small"
                                            placeholder={`Gap ${num}${num === 1 ? ' (required)' : ' (optional)'}`}
                                            value={form.section_b[`gap${num}`]}
                                            onChange={(e) => setGap(i, e.target.value)}
                                        />
                                    </Box>
                                ))}
                            </Stack>
                        </Box>

                        {/* Q9 */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h5" sx={{ mb: 1 }}>
                                <Box component="span" sx={{ color: "#00FFFF", fontWeight: 700 }}>09. </Box>
                                Changes / enhancements which you may suggest to the exercise scenarios.
                            </Typography>
                            <TextField
                                fullWidth multiline minRows={3} size="small"
                                placeholder="Your suggestions..."
                                value={form.section_b.scenario_changes}
                                onChange={(e) => setB("scenario_changes", e.target.value)}
                            />
                        </Box>

                        {/* Q10 */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h5" sx={{ mb: 1 }}>
                                <Box component="span" sx={{ color: "#00FFFF", fontWeight: 700 }}>10. </Box>
                                Feedback / suggestion on the platform used for the Cyber Exercise.
                            </Typography>
                            <TextField
                                fullWidth multiline minRows={3} size="small"
                                placeholder="Your feedback on the platform..."
                                value={form.section_b.platform_feedback}
                                onChange={(e) => setB("platform_feedback", e.target.value)}
                            />
                        </Box>

                        {/* Q11 */}
                        <Box>
                            <Typography variant="h5" sx={{ mb: 1 }}>
                                <Box component="span" sx={{ color: "#00FFFF", fontWeight: 700 }}>11. </Box>
                                Any additional task your team would have done, which was not executed during the exercise.
                            </Typography>
                            <TextField
                                fullWidth multiline minRows={3} size="small"
                                placeholder="Additional tasks or actions..."
                                value={form.section_b.additional_tasks}
                                onChange={(e) => setB("additional_tasks", e.target.value)}
                            />
                        </Box>

                    </Box>
                </Paper>

                {/* Submit */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <Button variant="contained" onClick={handleReset} disabled={loading} >
                        Reset
                    </Button>

                    <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                        {loading ? "Submitting..." : "Submit Feedback"}
                    </Button>
                </Box>

            </Box>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen
            TransitionComponent={Fade}
        >

            <DialogContent sx={{ p: 0, background: "#111218" }}>
                {submitted ? (
                    SuccessView
                ) : (
                    <Grid container alignItems="flex-start" sx={{ minHeight: "100%" }}>
                        {/* LEFT — 75% */}
                        <Grid
                            item
                            xs={12}
                            md={9}
                            sx={{
                                borderRight: "1px solid #1f2a33",
                                px: 8,
                            }}
                        >
                            {FormBody}
                        </Grid>

                        {/* RIGHT — 25% */}
                        <Grid
                            item
                            xs={12}
                            md={3}
                            sx={{
                                bgcolor: "#0B0F14",
                                p: 2.5,
                                position: "sticky",
                                top: 20,
                                height: "fit-content",
                                alignSelf: "flex-start"
                            }}
                        >
                            <ProfileCard_ttx
                                name={name}
                                scenario={scenario}
                                team={team}

                            />
                        </Grid>
                    </Grid>
                )}
            </DialogContent>


        </Dialog >
    );
}

// ── Usage example ─────────────────────────────────────────────────────────
// import FeedbackForm from "./FeedbackForm";
//
// const [open, setOpen] = useState(false);
//
// const handleSubmit = async (formData) => {
//   const res = await fetch("/api/feedback", {
//     method: "POST",
//     headers: { "Content-Type": "application/json" },
//     body: JSON.stringify(formData),
//   });
//   if (!res.ok) throw new Error("Submission failed.");
// };
//
// <Button onClick={() => setOpen(true)}>Open Feedback</Button>
// <FeedbackForm open={open} onClose={() => setOpen(false)} onSubmit={handleSubmit} />